import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  FaArrowLeft, 
  FaCheck, 
  FaExclamationTriangle,
  FaInfoCircle,
  FaComments
} from 'react-icons/fa';
import { supabase } from '../../../data/supabaseClient';
import { AuthContext } from '../../../Provider';
import { useToast } from '../../../hooks/ToastContext';
import Spinner from '../../../common/Spinner';
import { CustomButton2, CustomButton8 } from '../../../Templates';
import '../../styles/GroupsPage.css';
import '../../styles/CreateGroupPage.css';
import '../../styles/GroupDiscussionPage.css';

const CreateGroupDiscussion = () => {
  const { id } = useParams();
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const { addToast } = useToast();
  
  // State variables
  const [loading, setLoading] = useState(true);
  const [group, setGroup] = useState(null);
  const [userMember, setUserMember] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    error: null
  });
  
  // Load group data
  useEffect(() => {
    const fetchGroupData = async () => {
      try {
        setLoading(true);
        
        if (!user) {
          addToast({
            type: 'error',
            title: t('errors.authentication_required'),
            description: t('errors.login_required_action')
          });
          navigate('/login');
          return;
        }
        
        if (!id) {
          navigate('/groups');
          return;
        }
        
        // Fetch group details
        const { data: groupData, error: groupError } = await supabase
          .from('groups')
          .select('*')
          .eq('id', id)
          .single();
        
        if (groupError) throw groupError;
        
        setGroup(groupData);
        
        // Check if user is a member of the group
        const { data: memberData, error: memberError } = await supabase
          .from('group_members')
          .select('*')
          .eq('group_id', id)
          .eq('user_id', user.id)
          .eq('status', 'active')
          .single();
        
        if (memberError) {
          console.error('Error checking membership:', memberError);
          // If not a member, redirect back to the group page
          addToast({
            type: 'error',
            title: t('errors.access_denied'),
            description: t('errors.not_group_member')
          });
          navigate(`/groups/${id}`);
          return;
        }
        
        setUserMember(memberData);
        
      } catch (error) {
        console.error('Error fetching group data:', error);
        addToast({
          type: 'error',
          title: t('errors.error'),
          description: t('errors.group_fetch_failed')
        });
        navigate('/groups');
      } finally {
        setLoading(false);
      }
    };
    
    fetchGroupData();
  }, [id, user, navigate, t, addToast]);
  
  // Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
      error: null // Clear previous error when field is edited
    }));
  };
  
  // Validate form before submission
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.title.trim()) {
      newErrors.title = t('errors.title_required');
    }
    
    if (!formData.content.trim()) {
      newErrors.content = t('errors.content_required');
    }
    
    if (Object.keys(newErrors).length > 0) {
      setFormData(prev => ({
        ...prev,
        error: Object.values(newErrors)[0]
      }));
      return false;
    }
    
    return true;
  };
  
  // Create discussion
  const handleCreateDiscussion = async () => {
    if (!validateForm()) {
      addToast({
        type: 'error',
        title: t('errors.validation_error'),
        description: formData.error
      });
      return;
    }
    
    setIsCreating(true);
    
    try {
      // Create the discussion
      const { data: discussionData, error: discussionError } = await supabase
        .from('group_discussions')
        .insert({
          group_id: id,
          user_id: user.id,
          title: formData.title.trim(),
          content: formData.content.trim(),
          status: 'active'
        })
        .select()
        .single();
      
      if (discussionError) throw discussionError;
      
      addToast({
        type: 'success',
        title: t('success'),
        description: t('groups.discussion_created_success')
      });
      
      // Navigate to the discussion
      navigate(`/groups/${id}/discussion/${discussionData.id}`);
      
    } catch (error) {
      console.error('Error creating discussion:', error);
      addToast({
        type: 'error',
        title: t('errors.creation_failed'),
        description: t('errors.discussion_creation_failed')
      });
      setIsCreating(false);
    }
  };
  
  // Navigate back to group
  const handleBack = () => {
    navigate(`/groups/${id}`);
  };
  
  if (loading) {
    return (
      <>
        <div className="groups-page">
          <Spinner size="large" />
        </div>
      </>
    );
  }
  
  return (
    <>
      <div className="create-group-container">
        <div className="back-button" style={{ marginBottom: '20px' }}>
          <CustomButton8 onClick={handleBack}>
            <FaArrowLeft />
          </CustomButton8>
        </div>
        
        <h2 className="form-heading">{t('groups.create_discussion')}</h2>
        <p className="group-context">{t('groups.for_group')}: <strong>{group?.name}</strong></p>
        
        {formData.error && (
          <div className="error-message">
            <FaExclamationTriangle />
            <span>{formData.error}</span>
          </div>
        )}
        
        <div className="create-group-form">
          <div className="form-group">
            <label htmlFor="title">{t('groups.discussion_title')} *</label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleChange}
              placeholder={t('groups.discussion_title_placeholder')}
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="content">{t('groups.discussion_content')} *</label>
            <textarea
              id="content"
              name="content"
              value={formData.content}
              onChange={handleChange}
              placeholder={t('groups.discussion_content_placeholder')}
              rows={10}
            />
            <div className="form-hint">
              <FaInfoCircle />
              <span>{t('groups.discussion_hint')}</span>
            </div>
          </div>
          
          <div className="form-actions">
            <CustomButton2
              label={t('buttons.cancel')}
              onClick={handleBack}
              type="button"
            />
            <CustomButton2
              label={isCreating ? t('app.creating') : t('groups.create_discussion')}
              onClick={handleCreateDiscussion}
              disabled={isCreating}
              icon={isCreating ? <Spinner size="small" showText={false} /> : <FaComments />}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default CreateGroupDiscussion;
